/* eslint-disable max-len */
import React, { ReactNode, useState } from 'react'
import { useQueries, useQuery } from '@tanstack/react-query'
import { ErrorBoundary } from '../../../../components/common/ErrorBoundary'
import { useAppTranslation } from '../../../../hooks/useAppTranslation'
import { ViewBase } from '../../../../components/common/ViewBase'
import { SocietyWebsiteGeneralInformation } from '../../../../components/society/SocietyWebsite/SocietyWebsiteGeneralInformation'
import { SocietyWebsiteImages } from '../../../../components/society/SocietyWebsite/SocietyWebsiteImages'
import { SocietyWebsiteDocuments } from '../../../../components/society/SocietyWebsite/SocietyWebsiteDocuments'
import { SocietyWebsiteQuestionsAndAnswers } from '../../../../components/society/SocietyWebsite/SocietyWebsiteQuestionsAndAnswers'
import { SocietyWebsiteContact } from '../../../../components/society/SocietyWebsite/SocietyWebsiteContact'
import { useCurrentSociety } from '../../../../hooks/useCurrentSociety'
import { useAuthenticatedUserId } from '../../../../hooks/useAuthenticatedUserId'
import { isUserAdminInSociety } from '../../../../helpers/society'
import { ButtonVariant } from '../../../../components/common/Button'
import { IconChoices } from '../../../../components/common/Icon'
import { useModal } from '../../../../hooks/useModal'
import { CreateUpdateSocietyWebsiteFAQSectionModal } from '../../../../components/society/SocietyWebsite/SocietyWebsiteQuestionsAndAnswers/SocietyWebsiteQuestionsAndAnswersSection/CreateUpdateSocietyWebsiteFAQSectionModal'
import { useDocumentTitle } from '../../../../hooks/useDocumentTitle'
import { reverseDocumentTitle } from '../../../../navigation/reverseDocumentTitle'
import { websiteQueries } from '../../../../api/society-websites'
import { ISocietyWebsiteModel } from '../../../../interfaces/models/societies-websites.interfaces'
import { ISocietiesModel } from '../../../../interfaces/models/societies.interfaces'
import { mediaQueries } from '../../../../api/media'
import { i18nSocietyType } from '../../../../i18n'

export enum ActiveTabBar {
  GENERAL_INFORMATION = 0,
  IMAGES = 1,
  DOCUMENTS = 2,
  FAQ = 3,
  CONTACT = 4,
}

interface SocietyWebsiteTabBarProps {
  activeTab: ActiveTabBar
  website: ISocietyWebsiteModel
  society: ISocietiesModel
}

const SocietyWebsiteTabBar = ({
  activeTab,
  website,
  society,
}: SocietyWebsiteTabBarProps): ReactNode => {
  switch (activeTab) {
    case ActiveTabBar.GENERAL_INFORMATION:
      return <SocietyWebsiteGeneralInformation website={website} />
    case ActiveTabBar.IMAGES:
      return <SocietyWebsiteImages website={website} />
    case ActiveTabBar.DOCUMENTS:
      return <SocietyWebsiteDocuments website={website} society={society} />
    case ActiveTabBar.FAQ:
      return <SocietyWebsiteQuestionsAndAnswers website={website} />
    default:
      return <SocietyWebsiteContact website={website} />
  }
}

export const SocietyWebsiteView = (): React.ReactNode => {
  const { society, isLoading: isSocietyLoading } = useCurrentSociety()
  useDocumentTitle(
    reverseDocumentTitle('management:website', {
      '{{ societyName }}': society?.name,
    })
  )
  const { translate } = useAppTranslation()
  const userId = useAuthenticatedUserId()
  const [activeTab, setActiveTab] = useState<ActiveTabBar>(
    ActiveTabBar.GENERAL_INFORMATION
  )
  const isAdmin = isUserAdminInSociety(society, userId as string)
  const {
    show: showCreateSectionModal,
    open: openCreateSectionModal,
    close: closeCreateSectionModal,
  } = useModal()

  const tabs = [
    {
      title: translate('societyWebsiteView.tabBar.generalInformation'),
    },
    {
      title: translate('societyWebsiteView.tabBar.images'),
    },
    {
      title: translate('societyWebsiteView.tabBar.documents'),
    },
    {
      title: translate('societyWebsiteView.tabBar.questionsAndAnswers'),
    },
    {
      title: translate('societyWebsiteView.tabBar.contact'),
    },
  ]

  const { data: website, isLoading: isLoadingWebsite } = useQuery(
    websiteQueries.getWebsitePerSociety(society?._id)
  )

  useQuery(mediaQueries.getMedia(website?.pictureMainId))

  useQueries({
    queries:
      website?.societyPictures?.pictures.map((picture) =>
        mediaQueries.getMedia(picture.mediaId)
      ) || [],
  })

  const buttons = [
    ...(isAdmin
      ? [
          ...(activeTab === ActiveTabBar.FAQ
            ? [
                {
                  label: translate(
                    'createUpdateQuestionsAndAnswers.addNewSectionTitle'
                  ),
                  variant: ButtonVariant.PRIMARY,
                  icon: IconChoices.PLUS_SIGN,
                  onClick: openCreateSectionModal,
                },
              ]
            : []),
        ]
      : []),
  ]

  const getSubtitleText = (): JSX.Element => {
    return (
      <>
        <p className="mt-2 text-neutral-40">
          {translate('societyWebsiteView.subTitle', {
            context: i18nSocietyType(society?.societyTypeDisplayName),
          })}
        </p>
      </>
    )
  }

  const loading = isLoadingWebsite || isSocietyLoading

  return (
    <ErrorBoundary>
      <ViewBase
        title={translate('societyWebsiteView.title')}
        subtitle={getSubtitleText()}
        tabBar={{ tabs, setActiveTab, activeTab }}
        buttons={buttons}
        loading={loading}
      >
        {website && society && (
          <SocietyWebsiteTabBar
            society={society}
            activeTab={activeTab}
            website={website}
          />
        )}
      </ViewBase>
      {website && showCreateSectionModal && (
        <CreateUpdateSocietyWebsiteFAQSectionModal
          website={website}
          show={showCreateSectionModal}
          close={closeCreateSectionModal}
        />
      )}
    </ErrorBoundary>
  )
}
